import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CreditCard, Gift, Building2, Star, Crown, CheckCircle, Loader, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import RegistrationSidebar from '../../components/registration/RegistrationSidebar'
import StepIndicator from '../../components/registration/StepIndicator'
import { vendorService } from '../../services/vendorService'
import { useAuth } from '../../context/AuthContext'
import { loadRazorpayScript, createRazorpayOrder } from '../../utils/razorpay'
import { saveToLocalStorage, getFromLocalStorage, removeFromLocalStorage } from '../../utils/localStorage'
import benchLogo from '../../assets/Bench_logo.png'

const SubscriptionPlanPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, role, getAuthHeaders } = useAuth()
  const [selectedPlan, setSelectedPlan] = useState('Free')
  const [billingCycle, setBillingCycle] = useState('Monthly')
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [userId, setUserId] = useState(null)
  const [vendorId, setVendorId] = useState(null)
  const [promoCode, setPromoCode] = useState('')
  const [promoDiscount, setPromoDiscount] = useState(0)
  const [isStepCompleted, setIsStepCompleted] = useState(false)

  // Ref to prevent multiple simultaneous API calls
  const isCheckingRef = useRef(false)
  const hasNavigatedRef = useRef(false)

  // Check registration status on mount
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      // Prevent multiple simultaneous calls
      if (isCheckingRef.current || hasNavigatedRef.current) {
        return
      }

      // Only run if we're on the subscription page
      if (location.pathname !== '/vendor/registration/subscription') {
        return
      }

      try {
        isCheckingRef.current = true
        setIsCheckingStatus(true)
        const currentUser = await vendorService.getCurrentUser()
        
        if (!currentUser) {
          hasNavigatedRef.current = true
          toast.error('Please log in to continue')
          navigate('/auth/login')
          return
        }

        const currentUserId = currentUser.id || currentUser.user_id
        setUserId(currentUserId)

        // Get vendorId
        const currentVendorId = currentUser.vendor_id || currentUser.vendorId || sessionStorage.getItem('vendorId')
        if (currentVendorId) {
          setVendorId(currentVendorId)
        } else {
          // Step 1 not completed
          hasNavigatedRef.current = true
          toast.error('Please complete company details first')
          navigate('/vendor/registration/legal-details', { replace: true })
          return
        }

        // Check registration status
        const regStatus = currentUser.hasCompletedRegistration
        const isCompleted = regStatus === true || 
                           regStatus === 'true' || 
                           regStatus === 1 || 
                           regStatus === '1' ||
                           String(regStatus).toLowerCase() === 'true'
        
        // Check admin review status - only allow dashboard if approved
        const adminReview = currentUser.admin_review || currentUser.adminReview
        const userStatus = currentUser.status
        const bgvStatus = currentUser.BGV_status || currentUser.bgv_status

        if (isCompleted) {
          // Registration completed - check admin review status
          if (adminReview === 'approved' && userStatus === 'active') {
            // Fully approved - go to dashboard
            hasNavigatedRef.current = true
            navigate('/vendor/dashboard', { replace: true })
            return
          } else {
            // Completed but not approved - redirect to documents
            // But only if we're not already being redirected
            hasNavigatedRef.current = true
            navigate('/vendor/registration/documents', { replace: true })
            return
          }
        }

        // Navigation logic based on hasCompletedRegistration:
        // - true → Step 4 (Documents)
        // - "vendor-profile" → Step 3 (Subscription) - current step, allow editing
        // - "vendor-register" → Step 2 (Profile Building) - should redirect here
        // - false → Step 1 (Legal Details) - should redirect here
        
        if (regStatus === 'vendor-profile' || regStatus === 'vendor_profile') {
          // Step 2 completed - user is on correct page (subscription)
          // Allow them to continue and load existing subscription data
          setIsStepCompleted(true) // Mark step as completed
          if (currentVendorId && currentUserId) {
            try {
              // Use getVendorProfile to get subscription data (reuse currentUserId from above)
              const profileResponse = await vendorService.getVendorProfile(currentUserId)
              if (profileResponse?.success && profileResponse?.data) {
                const subscriptionInfo = profileResponse.data.Vendor_Subscription_Info || 
                                        profileResponse.data.vendor_subscription_info ||
                                        profileResponse.data.subscription
                
                if (subscriptionInfo) {
                  // Mark step as completed if subscription exists
                  setIsStepCompleted(true)
                  
                  // Map subscription data to form
                  const planName = subscriptionInfo.plan_name || subscriptionInfo.planName || subscriptionInfo.plan
                  const billingCycle = subscriptionInfo.billing_cycle || subscriptionInfo.billingCycle
                  const promoCode = subscriptionInfo.promo_code || subscriptionInfo.promoCode
                  
                  if (planName) {
                    // Map plan name to our plan IDs (Free, Professional, Enterprise)
                    const planMap = {
                      'Free Plan': 'Free',
                      'Professional Plan': 'Professional',
                      'Enterprise Plan': 'Enterprise'
                    }
                    setSelectedPlan(planMap[planName] || planName || 'Free')
                  }
                  
                  if (billingCycle) {
                    // Map billing cycle (monthly, yearly, etc.)
                    const cycleMap = {
                      'monthly': 'Monthly',
                      'yearly': 'Yearly',
                      'Monthly': 'Monthly',
                      'Yearly': 'Yearly'
                    }
                    setBillingCycle(cycleMap[billingCycle] || billingCycle || 'Monthly')
                  }
                  
                  if (promoCode) {
                    setPromoCode(promoCode)
                  }
                  
                  toast('Loaded subscription data', { icon: 'ℹ️', duration: 2000 })
                }
              }
            } catch (error) {
              console.error('Error loading subscription data:', error)
              // Non-blocking error - allow user to continue
            }
          }
        } else if (regStatus === 'vendor-register' || regStatus === 'vendor_register') {
          // Step 1 completed but step 2 not - redirect to profile (Step 2)
          hasNavigatedRef.current = true
          toast.error('Please complete profile setup first')
          navigate('/vendor/registration/profile', { replace: true })
          return
        } else if (regStatus === false || regStatus === 'false' || regStatus === null || regStatus === undefined) {
          // No registration started - redirect to step 1
          hasNavigatedRef.current = true
          toast.error('Please complete company details first')
          navigate('/vendor/registration/legal-details', { replace: true })
          return
        }

        // Load saved plan selection from localStorage (fallback)
        const savedPlan = sessionStorage.getItem('selectedSubscriptionPlan')
        const savedBilling = sessionStorage.getItem('selectedBillingCycle')
        if (savedPlan && !selectedPlan) {
          setSelectedPlan(JSON.parse(savedPlan))
        }
        if (savedBilling && !billingCycle) {
          setBillingCycle(JSON.parse(savedBilling))
        }
      } catch (error) {
        console.error('Error checking registration status:', error)
        toast.error('Error loading user information')
        hasNavigatedRef.current = true
        navigate('/auth/login')
      } finally {
        isCheckingRef.current = false
        setIsCheckingStatus(false)
      }
    }

    checkRegistrationStatus()
  }, [navigate, location.pathname])

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedData = getFromLocalStorage('vendor_registration_step3')
    if (savedData) {
      if (savedData.selectedPlan) setSelectedPlan(savedData.selectedPlan)
      if (savedData.billingCycle) setBillingCycle(savedData.billingCycle)
      if (savedData.promoCode) setPromoCode(savedData.promoCode)
    }
  }, [])

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    saveToLocalStorage('vendor_registration_step3', {
      selectedPlan,
      billingCycle,
      promoCode
    })
  }, [selectedPlan, billingCycle, promoCode])

  const plans = [
    {
      id: 'Free',
      name: 'Free Plan',
      description: 'Get started with basic features',
      price: { monthly: 0, yearly: 0 },
      features: [
        'Basic profile creation',
        'Up to 5 bench resources',
        'Limited project access',
        'Email support'
      ],
      icon: <Gift className="w-5 h-5" />
    },
    {
      id: 'Basic',
      name: 'Basic Vendor',
      description: 'Perfect for small vendors',
      price: { monthly: 999, yearly: 9999 },
      features: [
        'Up to 10 bench resources',
        'Basic project matching',
        'Email support',
        'Standard profile views'
      ],
      icon: <Building2 className="w-5 h-5" />
    },
    {
      id: 'Professional',
      name: 'Professional',
      description: 'For growing businesses',
      price: { monthly: 2999, yearly: 29999 },
      features: [
        'Up to 50 bench resources',
        'Advanced matching',
        'Priority support',
        'Enhanced visibility',
        'Advanced analytics'
      ],
      popular: true,
      icon: <Star className="w-5 h-5" />
    },
    {
      id: 'Enterprise',
      name: 'Enterprise',
      description: 'Complete solution',
      price: { monthly: 9999, yearly: 99999 },
      features: [
        'Unlimited resources',
        'AI-powered matching',
        '24/7 premium support',
        'Maximum visibility',
        'Complete analytics',
        'Dedicated manager'
      ],
      icon: <Crown className="w-5 h-5" />
    }
  ]

  const getSavings = (plan) => {
    const monthlyTotal = plan.price.monthly * 12
    const yearlyPrice = plan.price.yearly
    return monthlyTotal - yearlyPrice
  }

  const handleFreePlanSelection = async () => {
    try {
      setIsProcessingPayment(true)

      const subscriptionData = {
        user_id: userId,
        vendor_id: vendorId,
        role: 'vendor',
        plan_name: 'Free',
        billing_cycle: 'Monthly',
        payment_method: 'Free',
        payment_status: 'completed',
        amount: 0,
        currency: 'INR'
      }

      await vendorService.processSubscriptionPayment(subscriptionData)
      
      // Clear localStorage after successful submission
      removeFromLocalStorage('vendor_registration_step3')
      
      toast.success('Free plan activated successfully!')

      // Update registration status
      const userDataString = sessionStorage.getItem('userData')
      if (userDataString) {
        const userData = JSON.parse(userDataString)
        userData.hasCompletedRegistration = true
        sessionStorage.setItem('userData', JSON.stringify(userData))
      }

      navigate('/vendor/registration/documents')
    } catch (error) {
      console.error('Free plan activation error:', error)
      toast.error(error.message || 'Failed to activate free plan')
      throw error
    } finally {
      setIsProcessingPayment(false)
    }
  }

  const processRazorpayPayment = async () => {
    try {
      const sdkLoaded = await loadRazorpayScript()
      if (!sdkLoaded) {
        throw new Error('Razorpay SDK failed to load. Are you online?')
      }

      const plan = plans.find(p => p.id === selectedPlan)
      if (!plan) throw new Error('Please select a plan first')

      const amount = billingCycle === 'Monthly' ? plan.price.monthly : plan.price.yearly
      const finalAmount = amount - (amount * (promoDiscount / 100))

      setIsProcessingPayment(true)

      const userName = user?.name || 'User'
      const userEmail = user?.email || 'user@example.com'
      const userPhone = user?.phone || '+919876543210'

      const options = {
        key: 'rzp_test_RZxCQ4W3XvY664',
        currency: 'INR',
        amount: Math.round(finalAmount * 100),
        name: 'AJA Bench',
        description: `${selectedPlan} Plan - ${billingCycle} Subscription`,
        image: benchLogo,
        handler: async function (response) {
          const subscriptionData = {
            user_id: userId,
            vendor_id: vendorId,
            role: 'vendor',
            plan_name: selectedPlan,
            billing_cycle: billingCycle,
            payment_method: 'Razorpay',
            payment_status: 'completed',
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            promo_code: promoCode || undefined,
            amount: Math.round(finalAmount * 100),
            currency: 'INR'
          }

          try {
            await vendorService.processSubscriptionPayment(subscriptionData)
            
            // Clear localStorage after successful submission
            removeFromLocalStorage('vendor_registration_step3')
            
            toast.success('Payment successful! Subscription activated.')

            // Update registration status
            const userDataString = sessionStorage.getItem('userData')
            if (userDataString) {
              const userData = JSON.parse(userDataString)
              userData.hasCompletedRegistration = true
              sessionStorage.setItem('userData', JSON.stringify(userData))
            }

            navigate('/vendor/registration/documents')
          } catch (err) {
            console.error('Verification failed:', err)
            toast.error('Payment succeeded but verification failed. Please contact support.')
          } finally {
            setIsProcessingPayment(false)
          }
        },
        prefill: {
          name: userName,
          email: userEmail,
          contact: userPhone
        },
        theme: { color: '#2563eb' },
        modal: {
          ondismiss: function () {
            setIsProcessingPayment(false)
            toast.error('Payment was cancelled. You can try again.')
          }
        }
      }

      const paymentObject = new window.Razorpay(options)
      paymentObject.on('payment.failed', function (response) {
        setIsProcessingPayment(false)
        toast.error('Payment failed: ' + (response.error?.description || 'Unknown error'))
      })

      paymentObject.open()
    } catch (error) {
      console.error('Razorpay init error:', error)
      toast.error(error.message || 'Payment initialization failed')
      setIsProcessingPayment(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!userId || !vendorId) {
      toast.error('User not authenticated. Please log in again.')
      navigate('/auth/login')
      return
    }

    if (!selectedPlan) {
      toast.error('Please select a subscription plan')
      return
    }

    try {
      setIsLoading(true)

      if (selectedPlan === 'Free') {
        await handleFreePlanSelection()
      } else {
        await processRazorpayPayment()
      }
    } catch (error) {
      console.error('Error in subscription submission:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-secondary-600 dark:text-secondary-400">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />

      <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-4 gap-4">
        {/* Step Indicator - Outside the white card */}
        <div className="w-full max-w-6xl">
          <StepIndicator currentStep={3} />
        </div>

        <div className="flex items-stretch justify-center w-full gap-4">
          {/* Sidebar on the left */}
          <RegistrationSidebar currentStep={3} isStepCompleted={isStepCompleted} />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card w-full max-w-6xl p-5 md:p-6 shadow-glow flex-1 flex flex-col"
          >
            {/* Header */}
          <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
              Choose Your Plan
            </h1>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
              Select the perfect plan for your vendor business
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-5">
            <div className="bg-secondary-100 dark:bg-secondary-800 rounded-lg p-1 flex relative">
              <button
                type="button"
                onClick={() => setBillingCycle('Monthly')}
                className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors relative z-10 ${
                  billingCycle === 'Monthly'
                    ? 'bg-white dark:bg-secondary-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-secondary-700 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-secondary-100'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('Yearly')}
                className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors relative z-10 pr-12 ${
                  billingCycle === 'Yearly'
                    ? 'bg-white dark:bg-secondary-700 text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-secondary-700 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-secondary-100'
                }`}
              >
                Yearly
              </button>
              <span className="absolute -top-2 -right-2 bg-success-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md border-2 border-white dark:border-secondary-800 z-20">
                Save 17%
              </span>
            </div>
          </div>

          {/* Plans */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 items-stretch">
              {plans.map((plan) => {
                const price = billingCycle === 'Monthly' ? plan.price.monthly : plan.price.yearly
                const isSelected = selectedPlan === plan.id

                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`border-2 rounded-lg p-5 cursor-pointer transition-all flex flex-col min-h-[400px] ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md'
                        : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-700'
                    }`}
                  >
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-primary-600 text-white shadow-md'
                            : 'bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400'
                        }`}>
                          {plan.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                              {plan.name}
                            </h3>
                            {plan.popular && (
                              <span className="text-[10px] bg-primary-500 text-white px-2 py-0.5 rounded-full font-semibold">
                                Popular
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
                            {plan.description}
                          </p>
                          <div className="flex items-baseline gap-1 mb-1">
                            <span className="text-3xl font-bold text-secondary-900 dark:text-secondary-100">
                              ₹{price}
                            </span>
                            <span className="text-base text-secondary-500">
                              /{billingCycle === 'Monthly' ? 'month' : 'year'}
                            </span>
                          </div>
                          {billingCycle === 'Yearly' && plan.price.yearly > 0 && (
                            <p className="text-sm text-success-600 dark:text-success-400 font-semibold">
                              Save ₹{getSavings(plan)}/year
                            </p>
                          )}
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-secondary-300 dark:border-secondary-600'
                      }`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                    
                    {/* Benefits Section - Always visible */}
                    <div className="mt-auto pt-4 border-t-2 border-secondary-200 dark:border-secondary-700">
                      <h4 className={`text-sm font-semibold mb-3 ${
                        isSelected
                          ? 'text-primary-700 dark:text-primary-300'
                          : 'text-secondary-700 dark:text-secondary-300'
                      }`}>
                        Plan Benefits:
                      </h4>
                      <div className="space-y-2.5">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2.5">
                            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              isSelected 
                                ? 'text-primary-600 dark:text-primary-400' 
                                : 'text-secondary-400 dark:text-secondary-500'
                            }`} />
                            <span className={`text-sm leading-relaxed ${
                              isSelected
                                ? 'text-secondary-800 dark:text-secondary-200'
                                : 'text-secondary-700 dark:text-secondary-300'
                            }`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <button
                type="button"
                onClick={() => navigate('/vendor/registration/profile')}
                className="btn-outline h-9 text-xs font-semibold flex items-center gap-1.5 px-4"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back</span>
              </button>
              <button
                type="submit"
                disabled={isLoading || isProcessingPayment}
                className="btn-primary h-9 text-xs font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 px-6"
              >
                {isLoading || isProcessingPayment ? (
                  <>
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    <span>{selectedPlan === 'Free' ? 'Activating...' : 'Processing...'}</span>
                  </>
                ) : (
                  <>
                    <span>{selectedPlan === 'Free' ? 'Activate Free Plan' : 'Continue to Payment'}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPlanPage

